/* eslint-disable max-len */
import {
  Avatar,
  AvatarGroup,
  Box,
  Collapse,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { DownloadIcon, FileText, GraduationCap, PencilRuler, UserRoundCheck, UserRoundX } from "lucide-react";
import {
  useCallback,
  useEffect,
  useState,
  type KeyboardEvent,
  type MouseEvent
} from "react";
import { useTranslation } from "react-i18next";
import type { LearningActivity, ReferencedLearningActivity } from "types";
import { ActivityItem } from "./ActivityDotNavigation.types";
import {
  CardContent,
  CardWrapper,
  CtaContainer,
  MetadataRow,
  StatusBox,
  StepIndicator,
  SubtitleTypography,
  TitleRow,
  TitleTypography
} from "./CourseActivityCard.styles";
import type { CourseActivityCardProps } from "./CourseActivityCard.types";

import { isNotClassroom } from "./utils";
import { ButtonDigited } from "../../components/Button/Button.variants";
import { HiddenCTA } from "../../components/CarouselCardRestyle/HiddenCTA.component";
import { useDescendantClassrooms } from "../../components/DetailsEsploraRestyle/DescendantsClassrooms.context";
import { useActivityAndCourseSelection } from "../../components/DetailsEsploraRestyle/IndexActivitySelected.context";
import { LearningActivityModal } from "../../components/LearningActiviyModal/LearningActivityModal.component";
import { ctaDisabled } from "../../components/LearningActiviyModal/utils";
import { LEVEL } from "../../consts";
import { useType } from "../../hooks";
import { useDownloadCertificate, useEditionsQuery } from "../../queries";
import { useAuthStore } from "../../stores";
import { formatDuration_h_m } from "../../utils/datetime";
import { downloadBlobFile } from "../../utils/general";
import { StatusBoxesContainer } from "../CarouselCardRestyle/CarouselCard.style";
import { StatusBox as EditionBadge } from "../Commons/StatusBox.component";
import { NumberLOTypography } from "../PathCourseAccordion/PathCourseAccordion.styles";
import {
  canSelfEnroll,
  getEditionJoinAvailability,
  getEditionMeetingUrl,
  isEnrolledLoStatus,
  oneUserEdition
} from "../TabsContent/Editions/utils";

function getInitials(completeName: string): string {
  return completeName
    .split(" ")
    .filter(Boolean)
    .map((word) => word[0]?.toUpperCase())
    .slice(0, 2)
    .join("");
}

function parsePercentage(value?: string): number {
  if (!value) return 0;
  const parsed = parseInt(value.replace("%", ""), 10);
  return isNaN(parsed) ? 0 : parsed;
}

export function CourseActivityCard({
  activity,
  attributes,
  canAccessNow,
  certificate,
  courseId,
  disabled,
  duration,
  ecmRegistration,
  ecm_specialization,
  enrollType,
  externalFileUrl,
  grandParentId,
  id,
  index,
  isActive = false,
  isBlinking = false,
  isEditorial,
  isFavourite,
  isLinkRelative,
  isMandatory,
  isStandAlone,
  isSurvey,
  isTest,
  learningObjectType,
  learningObjectTypology,
  link,
  madeAttempts,
  parentId,
  percentageOfCompletion,
  rootId,
  setOpenModalEcm,
  status,
  teachers,
  title,
  toolNumber,
  isStandAloneActivity,
  onMouseEnter,
  handleActivityClick,
  initialActiveLoId
}: CourseActivityCardProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(500));
  const typeObjectLearning = useType(learningObjectType, learningObjectTypology);
  const [openEdition, setOpenEdition] = useState(false);
  const [_isDownloading, setIsDownloading] = useState(false);

  const userData = useAuthStore(state => state.userData);

  const { mutateAsync: mutateDownloadCertificate } = useDownloadCertificate();

  const { activityIdSelected, setActivityIdSelected, syncUrlToActivity } = useActivityAndCourseSelection();

  const showModalEcm = setOpenModalEcm && disabled && ecm_specialization && !ecmRegistration;

  const handleClick = useCallback(() => {
    if(activity?.id !== activityIdSelected) {
      setActivityIdSelected(activity?.id ?? undefined);
      syncUrlToActivity(activity as ActivityItem, isStandAloneActivity);
    }
  }, [activity, handleActivityClick, syncUrlToActivity]);

  useEffect(() => {
    initialActiveLoId && setActivityIdSelected(initialActiveLoId);
  }, [initialActiveLoId]);

  const handleDownloadCertificate = async () => {
    if (!certificate?.isEnable || !certificate?.id || !userData?.id_user) return;
    setIsDownloading(true);
    try {
      const blob = await mutateDownloadCertificate({
        certificateId: certificate.id,
        learnerId: userData.id_user as number,
        learningObjectId: id
      });
      downloadBlobFile({
        blobFile: blob,
        fileName: `issued_certificate_lo_${id}_learner_${userData.id_user}.pdf`
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const isCompleted = status === "C";
  //const isTestCompleted = (isTest || isSurvey) && isCompleted;
  const percentage = parsePercentage(percentageOfCompletion);

  const typologyLabel = isTest ? t("test") : isSurvey ? t("survey") : typeObjectLearning;

  // Aria label for accessibility (same logic as CarouselCardRestyle)
  let ariaLabel = title;
  if (typologyLabel) {
    ariaLabel = `${title}. ${t("typology_carousel")}:${typologyLabel}`;
  }
  if (percentage > 0) {
    ariaLabel = `${ariaLabel}. ${t("completion_percentage")} ${percentageOfCompletion}`;
  }
  if (duration) {
    ariaLabel = `${ariaLabel}. ${t("duration")}:${duration}`;
  }
  if (isCompleted) {
    ariaLabel = `${ariaLabel}. ${t("status_lo")}:${t("completed")}`;
  } else if (isMandatory) {
    ariaLabel = `${ariaLabel}. ${t("status_lo")}:${t("mandatory")}`;
  }

  useEffect(() => {
    isActive && syncUrlToActivity(activity as ActivityItem);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isClassroom = !isNotClassroom(learningObjectTypology);
  // Le editions da useEditionsQuery servono per il pulsante "partecipa" (join) dell'aula.
  const { data: editions } = useEditionsQuery({ classroomId: activity?.id.toString() ?? "", enabled: isClassroom });
  const enrolledEdition = editions?.find((edition) => edition.status === "E" && !edition.isAbsent);

  // Stato di iscrizione dell'utente dai descendants-classrooms (unica chiamata per pagina):
  // lo status delle edizioni è globale, quindi non dice nulla sull'utente.
  const { getDescendantClassroom, isUnavailable: descendantsUnavailable } = useDescendantClassrooms();
  const descendant = getDescendantClassroom(activity?.id);
  const descendantEditions = descendant?.editions ?? [];
  const enrollStatus = descendant?.enrollStatus;
  // `enrollStatus` resta "E" anche dopo la disiscrizione: descrive l'assegnazione dell'LO,
  // non l'iscrizione a un'edizione. Quella la dice `editionId`, che torna null appena
  // l'utente si disiscrive: servono entrambi per dire che l'utente è iscritto.
  const hasUserEdition = descendant?.editionId != null;
  const isEnrolledClassroom = hasUserEdition && (enrollStatus === "E" || enrollStatus === "P");
  const isCompletedClassroom = enrollStatus === "C";
  // `selfEnrollment` è il flag della singola aula: da solo non basta a dire che l'iscrizione è
  // top-down. `enrollType` porta la modalità di iscrizione ("TOP"/"TOP_DOWN"/"TOP_MANDATORY" solo
  // se l'utente non può iscriversi da sé; "PRE_AUTO"/"BOTTOM" per le auto-iscrizioni).
  const canSelfEnrollClassroom = canSelfEnroll(descendant?.selfEnrollment ?? false, descendant?.enrollType);
  // "Disponibili" = edizioni con posti liberi.
  const availableEditionsCount = descendantEditions
    .filter((edition) => (edition.totalPlaces - edition.occupiedPlaces) > 0).length;
  // Assente: assenza registrata su un'edizione, senza iscrizione/completamento.
  const wasAbsent = isClassroom && !!descendant && !isEnrolledClassroom && !isCompletedClassroom
    && descendantEditions.some((edition) => edition.isAbsent);

  // Se il dato per-utente non arriverà (chiamata fallita o non eseguita) si ricade sul criterio
  // basato sulle edizioni: meno preciso, ma evita di nascondere risorse e docenti a chi è iscritto.
  // Durante il caricamento si resta sul comportamento prudente (nascosti finché non si sa).
  const fallbackEnrolled = editions && editions.length > 0
    ? oneUserEdition(editions)
    : isEnrolledLoStatus(status);
  const hideStaffAndResources = isClassroom
    && (descendantsUnavailable ? !fallbackEnrolled : !isEnrolledClassroom);

  // Badge di stato (solo per aule con dati descendants disponibili):
  // - iscritto -> "Iscrizione effettuata"; assente -> "Assente";
  // - non iscritto + autoiscrizione -> "Necessaria iscrizione" + N edizioni;
  // - non iscritto + puro top-down -> "Presto verrai iscritto".
  const showEnrolledBadge = isClassroom && !!descendant && !isCompletedClassroom && isEnrolledClassroom;
  const needsSelfEnrollment = isClassroom && !!descendant && !isCompletedClassroom && !isEnrolledClassroom && !wasAbsent && canSelfEnrollClassroom;
  const isWaitingTopDownEnrollment = isClassroom && !!descendant && !isCompletedClassroom && !isEnrolledClassroom && !wasAbsent && !canSelfEnrollClassroom;
  // CTA "Vedi le edizioni": solo se auto-iscrivibile con edizioni disponibili.
  const showEditionsCta = canSelfEnrollClassroom && !isEnrolledClassroom && !isCompletedClassroom && availableEditionsCount > 0;
  const waitingLabelKey = learningObjectTypology === LEVEL.DA_PHYSICAL_CLASS ? "physical_class_pending" : "virtual_class_pending";

  const isVirtualClassroom = learningObjectTypology === LEVEL.DA_VIRTUAL_CLASS;
  const joinAvailability = isClassroom && enrolledEdition
    ? getEditionJoinAvailability(enrolledEdition, isVirtualClassroom)
    : "not_yet";
  const canJoinClassroom = joinAvailability === "available";
  const joinTooltipKey = joinAvailability === "ended" ? "join_no_longer_available" : "join_not_available_yet";
  const handleJoinClassroom = () => {
    if (!enrolledEdition) return;
    const url = getEditionMeetingUrl(enrolledEdition);
    if (url) window.open(url, "_blank", "noreferrer");
  };

  return (
    <CardWrapper
      role="region"
      className={ "course-activity-card" }
      tabIndex={ 0 }
      aria-label={ ariaLabel }
      $isActive={  activityIdSelected === activity?.id }
      $isComplete={ isCompleted }
      sx={ isBlinking ? {
        "@keyframes heroCtaBlink": {
          "0%, 100%": { boxShadow: "inset 0 0 0 0 transparent" },
          "50%": { boxShadow: `inset 0 0 0 3px ${theme.customColors.borderAccent}` }
        },
        animation: "heroCtaBlink 0.6s ease-in-out 3"
      } : undefined }
      onClick={ (e: MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        handleClick();
        onMouseEnter && onMouseEnter(activity?.id);
        if (showModalEcm) {
          setOpenModalEcm(true);
        }
      } }
      onKeyDown={ (e: KeyboardEvent<HTMLElement>) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick();

          if (showModalEcm) {
            setOpenModalEcm?.(true);
          }
        }
      } }
    >
      { /* Step indicator with circular progress */ }
      { /* Card body */ }
      <CardContent
        isStart={ true }
        id={ `card-content-${activity?.id}` }
        item
        xs
        width={ "100%" }
        gap={ "1rem" }
      >
        <Stack className="info-row"
          flexDirection={ "row" }
          alignItems={ "center" }
          gap={ "1rem" }
          width={ "100%" }
          sx={ {
            cursor: "pointer"
          } }
        >
          <StepIndicator>
            <Box position="relative" display="inline-flex">
              <Box alignSelf="center" display="flex" flexShrink={ 0 }>
                <NumberLOTypography
                  sx={ {
                    minWidth: 24,
                    textAlign: "center"
                  } }>
                  { index }
                </NumberLOTypography>
              </Box>
            </Box>
          </StepIndicator>
          <Stack
            flex={ 1 }
            minWidth={ 0 }
          >
            <TitleRow>
              <TitleTypography
                color={ theme.customColors.exploreCardText }
              >
                { title }
              </TitleTypography>

            </TitleRow>

            <SubtitleTypography
              color={ theme.customColors.exploreCardText }
            >
              { typologyLabel }
              { duration ? ` | ${formatDuration_h_m(duration)}` : "" }
            </SubtitleTypography>
          </Stack>

        </Stack>
      </CardContent>

      <StatusBox sx={ { alignItems: "center", paddingY: theme.spacing(1) } }>
        { showEnrolledBadge && (
          <EditionBadge
            id={ `edition-enrolled-${id}` }
            backgroundColor={ theme.customColors.backgroundSecondary }
            border={ `1px solid ${theme.customColors.textTag}` }
            color={ theme.customColors.textTag }
            icon={ <UserRoundCheck size={ "0.875rem" } color={ theme.customColors.textTag } /> }
            label={ t("enrollment-success") }
          />
        ) }
        { isWaitingTopDownEnrollment && (
          <EditionBadge
            id={ `edition-waiting-${id}` }
            backgroundColor={ theme.customColors.backgroundSecondaryAlternative }
            border={ `1px solid ${theme.customColors.backgroundAccent}` }
            color={ theme.customColors.textTag }
            icon={ <PencilRuler size={ "0.875rem" } color={ theme.customColors.textTag } /> }
            label={ t(waitingLabelKey) }
          />
        ) }
        { wasAbsent && (
          <EditionBadge
            id={ `edition-absent-${id}` }
            backgroundColor={ theme.customColors.backgroundSecondaryAlternative }
            border={ `1px solid ${theme.customColors.borderWarning}` }
            color={ theme.customColors.textTag }
            icon={ <UserRoundX size={ "0.875rem" } color={ theme.customColors.textTag } /> }
            label={ t("absent") }
          />
        ) }
        { needsSelfEnrollment && (
          <EditionBadge
            id={ `edition-enrollment-required-${id}` }
            backgroundColor={ theme.customColors.backgroundSecondaryAlternative }
            border={ `1px solid ${theme.customColors.backgroundAccent}` }
            color={ theme.customColors.textTag }
            icon={ <PencilRuler size={ "0.875rem" } color={ theme.customColors.textTag } /> }
            label={ t("enrollment_required") }
          />
        ) }
        <StatusBoxesContainer
          attributes={ attributes }
          endDate={ undefined }
          expirationDate={ undefined }
          hideMandatoryAndAttributes
          id={ id }
          isCompleted={ isCompleted }
          inProgress={ status === "P" }
          isExpired={ false }
          isMandatory={ isMandatory }
          learningObjectTypology={ learningObjectTypology }
          startDate={ undefined }
        />
      </StatusBox>
      <Box
        className="card-bottom-box"
        display="flex"
        flexDirection="column"
        alignItems="flex-end"
        flexShrink={ 0 }
        justifyContent="space-between"
        alignSelf="stretch"
        sx={ {
          mt: "auto",
          width: "100%",
          paddingLeft: "40px"
        } }
      >
        <Collapse
          in={ activityIdSelected === activity?.id }
          timeout={ 150 }
          easing="ease-in-out"
          sx={ {
            width: "100%",
            "& .MuiCollapse-wrapper": {
              width: "100%"
            },
            "& .MuiCollapse-wrapperInner": {
              alignItems: "center",
              display: "flex",
              flexWrap: "wrap",
              gap: "1rem",
              justifyContent: "space-between",
              paddingTop: theme.spacing(2),
              transition: "all .15s ease-in-out",
              width: "100%",
              [theme.breakpoints.up("sm")]: {
                flexWrap: "nowrap"
              }
            }
          } }
        >
          <MetadataRow
            id={ `meta-data-activity-${id}` }
          >
            { needsSelfEnrollment && availableEditionsCount > 0 && (
              <Stack direction="row" alignItems="center" gap={ 0.5 }>
                <PencilRuler
                  aria-hidden="true"
                  size={ "1rem" }
                  color={ theme.customColors.exploreCardText }
                />
                <Typography
                  fontSize={ "0.75rem" }
                  variant="body2"
                  color={ theme.customColors.exploreCardText }
                >
                  { availableEditionsCount } { " " } { t("editions") }
                </Typography>
              </Stack>
            ) }
            { toolNumber > 0 && !hideStaffAndResources && (
              <Stack direction="row" alignItems="center" gap={ 0.5 }>
                <FileText
                  aria-hidden="true"
                  size={ "1rem" }
                  color={ theme.customColors.exploreCardText }
                />
                <Typography
                  fontSize={ "0.75rem" }
                  variant="body2"
                  color={ theme.customColors.exploreCardText }
                >
                  { t("resource", { count: toolNumber }) }
                </Typography>
              </Stack>
            ) }

            { teachers && teachers.length > 0 && !hideStaffAndResources && (
              <Stack direction="row" alignItems="center" gap={ 1 }>
                <GraduationCap
                  aria-hidden="true"
                  size={ "1rem" }
                  color={ theme.customColors.exploreCardText }
                />
                <Typography
                  variant="body2"
                  fontSize={ "0.75rem" }
                  color={ theme.customColors.exploreCardText }>
                  { t("teachers") }
                </Typography>
                <AvatarGroup
                  max={ 4 }
                  sx={ {
                    "& .MuiAvatar-root": {
                      border: `2px solid ${theme.customColors.backgroundPrimary}`,
                      fontSize: "0.5rem",
                      height: "1.25rem",
                      lineHeight: "0.75rem",
                      width: "1.25rem"
                    }
                  } }
                >
                  { teachers.map((teacher) => (
                    <Avatar
                      key={ teacher.id }
                      alt={ teacher.completeName }
                      src={ teacher.image || undefined }
                      sx={ {
                        bgcolor: theme.customColors.backgroundPrimaryCta
                      } }
                    >
                      { getInitials(teacher.completeName) }
                    </Avatar>
                  )) }
                </AvatarGroup>
              </Stack>
            ) }

            <Stack
              flexDirection={ "row" }
              gap="0.75rem"
            >
              { certificate && (
                <DownloadIcon
                  size={ "1.25rem" }
                  color={ certificate.isEnable
                    ? theme.customColors.exploreCardText
                    : theme.customColors.border }
                  style={ {
                    cursor: certificate.isEnable ? "pointer" : "not-allowed"
                  } }
                  aria-label={ t("download") }
                  role="button"
                  tabIndex={ 0 }
                  onClick={ (e) => {
                    e.stopPropagation();
                    handleDownloadCertificate();
                  } }
                />
              ) }
            </Stack>
          </MetadataRow>
          <CtaContainer sx={ {
            "& .card-cta": {
              gap: 0
            },
            "& .card-cta > .MuiStack-root:empty": {
              display: "none"
            },
            "& .collapse_cta": {
              gap: 0,
              height: "auto",
              opacity: 1
            },
            alignItems: "flex-end",
            flexDirection: "column",
            gap: 0
          } }>
            <Stack
              direction="row"
              spacing="0.5rem"
              gap={ isMobile ? "0.5rem" : "unset" }
              flexWrap={ "wrap" }
            >
              { isNotClassroom(learningObjectTypology) ? (
                <>
                  <HiddenCTA
                    attributes={ attributes }
                    canAccessNow={ canAccessNow }
                    courseId={ courseId }
                    disabled={ ctaDisabled(activity as LearningActivity) || disabled }
                    ecm_specialization={ ecm_specialization ? ecm_specialization : false }
                    ecmRegistration={ ecmRegistration ? ecmRegistration : false }
                    enrollType={ enrollType }
                    externalFileUrl={ externalFileUrl }
                    grandParentId={ grandParentId ? grandParentId : undefined }
                    id={ id }
                    isCard={ false }
                    isEditorial={ Boolean(isEditorial) }
                    isLinkRelative={ isLinkRelative }
                    isStandAlone={ isStandAlone }
                    isSurvey={ isSurvey }
                    isHeroBanner={ false }
                    isTest={ isTest }
                    learningObjectType={ learningObjectType }
                    learningObjectTypology={ learningObjectTypology }
                    link={ link }
                    //TODO isfavourite lasciamo a false?
                    isFavourite={ !!isFavourite }
                    madeAttempts={ madeAttempts }
                    parentId={ parentId ? parentId : undefined }
                    percentageOfCompletion={ percentageOfCompletion }
                    rootId={ rootId }
                    showDetailsButton={ false }
                    status={ status || "N" }
                  />
                  { !isEditorial &&
                    <LearningActivityModal open={ openEdition } setOpen={ setOpenEdition } buttonType={ "secondary" } buttonDisabled={ Boolean(disabled) } activity={ activity as ReferencedLearningActivity }></LearningActivityModal> }
                </>
              ) : (
                <>
                  { isVirtualClassroom && enrolledEdition && (
                    <Tooltip title={ canJoinClassroom ? "" : t(joinTooltipKey) }>
                      <span style={ { display: "inline-flex" } }>
                        <ButtonDigited
                          id={ `activity-join-button-${id}` }
                          data-testid="activity-join-button"
                          aria-label={ t("join") }
                          typology="primary"
                          disabled={ !canJoinClassroom || Boolean(disabled) }
                          onClick={ (e) => {
                            e.stopPropagation();
                            handleJoinClassroom();
                          } }
                        >
                          { t("join") }
                        </ButtonDigited>
                      </span>
                    </Tooltip>
                  ) }
                  <LearningActivityModal
                    open={ openEdition }
                    setOpen={ setOpenEdition }
                    buttonType={ showEditionsCta ? "primary" : "secondary" }
                    buttonLabel={ showEditionsCta ? t("editions_show") : t("details") }
                    buttonDisabled={ Boolean(disabled) }
                    activity={ activity as ReferencedLearningActivity }
                    initialEditions={ editions }
                  />
                </>
              ) }
            </Stack>
          </CtaContainer>
        </Collapse>
      </Box>

    </CardWrapper>
  );
}
